import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Shield, UserCheck, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../hooks/useSettings';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const { settings, loading: settingsLoading, settingsLoaded, safeSettings } = useSettings();

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    // Do not redirect automatically; let AuthContext verify
    if (adminToken) {
      // stay on page to allow explicit admin login or token verification
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await adminLogin(username, password);
      if (result.success) {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        if (token) localStorage.setItem('admin_token', token);
        if (result.isSuperadmin || result.adminRole === 'superadmin') {
          navigate('/admin/dashboard', { replace: true });
        } else if (result.adminRole === 'user_manager') {
          navigate('/user-manager/dashboard', { replace: true });
        } else if (result.adminRole === 'partner_admin') {
          navigate('/partner-admin/dashboard', { replace: true });
        } else {
          navigate('/admin/dashboard', { replace: true });
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-10">
          <div className="flex justify-center items-center mb-6">
            {!settingsLoading && settings?.product_logo ? (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-600 rounded-2xl blur opacity-20"></div>
                <img 
                  src={settings.product_logo} 
                  alt={settings.product_name}
                  className="h-16 w-auto relative z-10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-600 rounded-2xl blur opacity-20"></div>
                <div className="relative z-10 bg-gradient-to-r from-purple-500 to-blue-600 text-white p-4 rounded-2xl shadow-lg">
                  <Shield size={32} />
                </div>
              </div>
            )}
          </div>
          <div className={`app-name-container ${settingsLoaded ? 'loaded' : ''}`}>
            <h1 className="text-3xl font-bold text-gray-800 mb-3">{safeSettings?.product_name}</h1>
          </div>
          {!settingsLoaded && (<div className="app-name-placeholder mb-3" style={{height: '2.5rem'}}></div>)}
          <div className="flex items-center justify-center gap-2 mb-2">
            <UserCheck size={18} className="text-purple-600" />
            <p className="text-gray-600 font-medium">Admin Dashboard Access</p>
          </div>
          <p className="text-gray-500 text-sm">Secure administrative portal</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <User size={16} className="text-purple-600" />
              Admin Username
            </label>
            <div className="relative">
              <input 
                type="text" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                className="form-input" 
                placeholder="Enter your admin username" 
                required 
              />
              <User className="input-icon" size={20} />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <KeyRound size={16} className="text-purple-600" />
              Admin Password
            </label>
            <div className="relative">
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="form-input" 
                placeholder="Enter your secure password" 
                required 
              />
              <Lock className="input-icon" size={20} />
            </div>
          </div>

          <button type="submit" className="btn-primary w-full mt-8" disabled={loading}>
            {loading ? (
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Authenticating...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Shield size={20} />
                <span>Access Admin Portal</span>
              </div>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;